import React, { useEffect, useMemo, useState } from "react";
import { useReceiptData } from "../handlers/useReceiptData";
import ReceiptHeader from "../ReceiptHeader";
import { Snackbar } from "@mui/material";
import AlertBar from "../../common/alert";
import LoadingModal from "../LoadingModal";
import QRPaymentCard from "./QRPaymentCard";
import PaymentSuccessCard from "./PaymentSuccessCard";
import PaymentExpiredCard from "./PaymentExpiredCard";

const QRPaymentPage: React.FC = () => {
  const [paymentHandled, setPaymentHandled] = useState(false);
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [alert, setAlert] = useState({
    show: false,
    severity: "info",
    title: "",
    message: "",
  });
  const { data } = useReceiptData(setAlert);

  const status = useMemo(() => {
    switch (data?.status) {
      case "PENDING_PAYMENT":
      case "SUCCESS":
      case "EXPIRED":
      case "CANCELLED":
        return data.status;
      default:
        return "UNKNOWN";
    }
  }, [data?.status]);

  useEffect(() => {
    // SUCCESS will show a new screen after payment
    // so just close loading modal then
    if (data?.status === "SUCCESS") {
      setShowLoadingModal(false);
    }
  }, [data?.status]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (paymentHandled) return;
      if (!event.data) return;

      if (
        event.data.type === "PAYMENT_SUCCESS" ||
        event.data.type === "PAYMENT_FAILED"
      ) {
        setAlert({
          show: true,
          severity: event.data.status === "success" ? "success" : "error",
          title: event.data.status === "success" ? "Success" : "Error",
          message:
            event.data.message ||
            (event.data.status === "success"
              ? "Payment succeeded."
              : "Payment failed."),
        });
        if (event.data.type === "PAYMENT_SUCCESS") {
          // show the modal while waiting for the receipt to update
          setShowLoadingModal(true);
        }
        setPaymentHandled(true);
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [paymentHandled]);

  return (
    <div className="bg-nobel-50 min-h-screen w-full flex flex-col content-center mx-auto">
      <div className="shrink-0">
        <ReceiptHeader data={data} />
      </div>
      <div className="flex flex-col w-full max-w-7xl mx-auto">
        <div className="w-full">
          {status === "PENDING_PAYMENT" && (
            <QRPaymentCard data={data} paymentCompleted={paymentHandled} />
          )}
          {status === "SUCCESS" && <PaymentSuccessCard data={data as any} />}
          {["EXPIRED", "CANCELLED"].includes(status) && <PaymentExpiredCard />}
        </div>
      </div>
      <Snackbar
        open={!!alert.show}
        sx={{ width: "95%" }}
        autoHideDuration={60000} // 60s
        onClose={() => setAlert({ ...alert, show: false })}
      >
        <AlertBar
          severity={alert.severity as any}
          show={!!alert.show}
          title={alert.title}
          message={typeof alert.message === "string" ? alert.message : ""}
          onClose={() => setAlert({ ...alert, show: false })}
        />
      </Snackbar>
      <LoadingModal open={showLoadingModal} />
    </div>
  );
};

export default QRPaymentPage;
